/**
 * Stage S23 — stylesheet generation (generateCSS).
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.13 (R-316…R-320) and §3.4 (R-049…R-073).
 *
 * The stylesheet is a **pure function of the extracted document settings**. It never sees
 * the HTML, so an identical preamble always yields byte-identical CSS (R-316). With the
 * reference preamble that is 16,062 characters / 16,065 UTF-8 bytes / 815 lines; with an
 * empty preamble box, 16,051 bytes (C-3 — the 3-byte delta is the á/í in the "Bahá'í" and
 * "Kitáb-i-Aqdas" section comments).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * G-19 — WHY THIS TEMPLATE IS String.raw
 * ─────────────────────────────────────────────────────────────────────────────────────
 * Six lines of the source's CSS template contain backslashes that JS template-literal
 * escaping cooks away *before the stylesheet is ever emitted*. The published stylesheet
 * has therefore never contained the LaTeX the source appears to write. The port must
 * reproduce the EMITTED form, not the source form:
 *
 *   source "LaTeX \LARGE size"           emits  "LaTeX LARGE size"
 *   source "like \ts{h})"                emits  "like " + <TAB> + "s{h})"
 *   source "The base \\appel"            emits  "The base \appel"
 *   source "the original \\appel"        emits  "the original \appel"
 *   source 'content: "\\00a0"'           emits  'content: "\00a0"'
 *
 * Only the last is functional CSS; the rest are cosmetic damage inside comments. The \LARGE
 * artefact costs a character and is still there. A sixth line, "for \noindent\hfil pattern",
 * was corrupted the same way and *added a line*; it was REPAIRED on 2026-09-07, so the emitted
 * stylesheet is one line shorter than the 815 quoted above.
 *
 * Writing the template with String.raw makes the bytes in this file **be** the bytes
 * emitted: the real <TAB> at stylesheet line 341 and the three
 * single backslashes are all present here literally, with no second escaping layer to
 * reason about. Do NOT convert this to a cooked template literal — "\00a0" would
 * silently become "00a0" and .french-spacing::before would stop working.
 *
 * One toolchain dependency comes with that: \0 followed by a digit is not a legal escape
 * in a template literal, and is tolerated only because this is a TAGGED template (the
 * ES2018 template-literal revision leaves the cooked value undefined and keeps the raw
 * one). tsconfig.app.json targets ES2022, so this is safe; lowering the target below
 * ES2018 would turn it into a syntax error. Verified end to end: an esbuild
 * bundle+minify of this module still emits the golden byte for byte.
 *
 * Duplicated and dead rules that are part of the byte contract — DO NOT DEDUPE:
 *   • G-18   sup { top: -0.4em } is declared twice.
 *   • .marginpar is declared twice — the first at 0.75em, the second at 0.7em. The SECOND
 *            wins the cascade and is the one that carries production.
 *   • .ltr-initial / .ltr-rest WERE declared twice — consolidated 2026-08-31 into the
 *     "Visual Separators" block, which is now their only declaration.
 *   • The whole .appel-* block is declared twice.
 *   • .page-break is declared twice inside the print media query (R-319).
 *   • R-319  page-orientation: landscape is not a CSS property. Inert, kept.
 *   • DF-12  hyphens: auto is emitted unconditionally, even when the preamble asked for
 *            no hyphenation (settings.noHyphenation is parsed and never read).
 *   • DF-11  there is no box-sizing declaration anywhere, and --latex-margin-left is
 *            emitted but never referenced (body pads both sides with margin-right).
 */
/**
 * PREAMBLE LINKS — every `PREAMBLE LINK:` note in this file sits on the rule it describes, not in
 * this header (CLAUDE.md, "Preamble and converter are documented both ways"). Each one has a
 * matching `% CONVERTISSEUR:` note on its line in `preambuleV3.tex`; change one side and you must
 * change the other. `grep -n "PREAMBLE LINK:" src/lib/converter/css.ts` lists them.
 *
 * /!\ WHY THE TEMPLATE BELOW IS SPLIT INTO CONCATENATED `String.raw` SEGMENTS.
 * A comment written *inside* the template is a CSS comment: it is emitted into the stylesheet the
 * translators download as `latex-styles.css` and into every generated `<style>` block. Splitting
 * the template lets each note sit beside its own rule as an ordinary JS comment that never ships.
 * The segments concatenate to the identical byte sequence — 16,065 bytes for the reference
 * preamble (see the C-3 note above). If you add a split, re-check that count.
 */
import { buildGoogleFontsUrl, resolveMonoFont } from './fonts'
import type { DocumentSettings } from './types'

/**
 * S23 — build the stylesheet for a set of extracted document settings.
 *
 * The 18 interpolation sites and their defaults are R-317
 *
 *   pageWidth        settings.pageWidth        || '172mm'  (A7, 2026-09-07 — was the constant
 *                    '172mm', which overrode the parsed value)
 *   marginTop        settings.marginTop        || '20mm'
 *   marginLeft       settings.marginLeft       || '25mm'   (emitted, never consumed)
 *   marginRight      settings.marginRight      || '20mm'   (pads BOTH sides of body)
 *   parSkip          settings.parSkip          || '0.75em'
 *   parIndent        settings.parIndent        || '1.5em'
 *   parSep           settings.parSep           || '0.25em'
 *   lineHeight       settings.lineSpread ? lineSpread * 1.2 : 1.6      (1.1 → 1.32)
 *   textAlign        settings.textAlign        || 'justify' (R-033: p forces justify)
 *   marginParWidth   settings.marginParWidth   || '20mm'
 *   leftMarginQuote  settings.leftMarginQuote  || parIndent
 *   colonSpace       parseFloat(frenchColonSpace) * 0.5 + 'em', else '0.25em'  (DF-13)
 *   thinSpace        parseFloat(frenchThinSpace)  * 0.5 + 'em', else '0.25em'  (DF-13)
 *   monoFont         resolveMonoFont(fonts)   — fonts.monoFont || the JetBrains stack
 *   fontUrl          buildGoogleFontsUrl(fonts)
 *   fonts.mainFont
 *   settings.footnoteSpacing || '0'
 *   settings.endnoteSpacing  || '0'
 *
 * || (not ??) is deliberate throughout: an empty-string setting falls back to the
 * default, exactly as the source does.
 */
export function generateCSS(settings: DocumentSettings): string {
  const fonts = settings.fonts

  // Both live in ./fonts, which owns the font layer. With the
  // reference preamble the URL is exactly:
  //   https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,400&family=JetBrains+Mono:wght@400;500&display=swap
  const fontUrl = buildGoogleFontsUrl(fonts)
  const monoFont = resolveMonoFont(fonts)

  // PREAMBLE LINK: \geometry{paper=a4paper,total={160mm,247mm},inner=25mm,top=25mm}
  // (preambuleV3.tex:107). READ AND HONOURED since 2026-09-07 (A7 / RK-34). The requested width
  // lands on --latex-page-width and therefore on `body { max-width }`, so every line wraps where
  // the printed edition wraps. WAS `const pageWidth = '172mm'` — a constant that overrode a
  // correctly parsed 160mm and made every line of every book 12mm wider than its edition.
  //
  // THE FALLBACK IS AN APP DEFAULT, NOT A LATEX ONE. LaTeX's own \textwidth is computed from
  // document class, paper size and point size together (size10/11/12.clo), so there is no single
  // number to be faithful to and inventing one is what CLAUDE.md §2 forbids. 172mm is the constant
  // this converter has always used when a document declares no \geometry, and it stays for exactly
  // that case — which is why nothing without a \geometry moves.
  const pageWidth = settings.pageWidth || '172mm'
  const marginTop = settings.marginTop || '20mm'
  const marginLeft = settings.marginLeft || '25mm' // emitted, never consumed
  const marginRight = settings.marginRight || '20mm'
  const parSkip = settings.parSkip || '0.75em'
  const parIndent = settings.parIndent || '1.5em'
  const parSep = settings.parSep || '0.25em' // list item spacing
  const lineHeight = settings.lineSpread ? settings.lineSpread * 1.2 : 1.6
  // PREAMBLE LINK: this is NOT where preambuleV3.tex's \sloppy ends up — nothing derives a
  // value from \sloppy at all (it is only deleted, see commands-text.ts R-279). settings.textAlign
  // comes from \raggedright, a different command meaning the opposite. It feeds
  // --latex-text-align, whose sole consumer is the `.extract` rule below — and no code anywhere
  // emits `class="extract"`, so this whole chain currently styles nothing. Paragraphs are forced
  // by `p { text-align: justify !important }` regardless.
  const textAlign = settings.textAlign || 'justify'
  const marginParWidth = settings.marginParWidth || '20mm' // verse-number column
  const leftMarginQuote = settings.leftMarginQuote || parIndent

  // DF-13: parsed from \FBsetspaces, then never applied to any text.
  const colonSpace = settings.frenchColonSpace
    ? `${parseFloat(settings.frenchColonSpace) * 0.5}em`
    : '0.25em'
  const thinSpace = settings.frenchThinSpace
    ? `${parseFloat(settings.frenchThinSpace) * 0.5}em`
    : '0.25em'

  // String.raw: see the G-19 note at the top of this file.
  return String.raw`/* LaTeX to HTML Styles */
@import url('${fontUrl}');

:root {
  --latex-text: #1a1a1a;
  --latex-bg: #ffffff;
  --latex-link: #0066cc;
  --latex-border: #cccccc;
  --latex-code-bg: #f5f5f5;
  --latex-ornament: #ad9160;
` +
  // PREAMBLE LINK: \usepackage{merriweather} (preambuleV3.tex:6). READ AND HONOURED — the one
  // \usepackage in the whole preamble that reaches the output. fonts.ts maps the brace body
  // through FONT_PACKAGE_MAP to 'Merriweather', Georgia, serif and to the Google Fonts @import
  // at the top of this stylesheet. Editing that preamble line really does change the typeface of
  // every document. /!\ The lookup key is the WHOLE brace body, so \usepackage{merriweather,xyz}
  // matches nothing and silently falls back to Source Serif 4.
  String.raw`  --latex-main-font: ${fonts.mainFont};
  --latex-mono-font: ${monoFont};
  --latex-page-width: ${pageWidth};
  --latex-margin-top: ${marginTop};
` +
  // PREAMBLE LINK: \geometry{…,inner=25mm,…} (preambuleV3.tex:107). PARSED SINCE 2026-09-07,
  // STILL CONSUMED BY NOTHING — and the second half of that is a decision, not an oversight.
  // R-021 now genuinely reads `inner`, so the value below is the requested 25mm rather than the
  // default it used to coincide with. But no rule references var(--latex-margin-left): `body` pads
  // BOTH sides with --latex-margin-right (`padding: 0 X` is the two-value shorthand), so the
  // asymmetric inner/outer margin cannot be expressed (DF-11, still open).
  // /!\ CONNECTING IT IS NOT A ONE-LINE FIX AND IS NOT THIS COMMIT'S BUSINESS. `inner`/`outer` mean
  // binding-side vs page-edge in a bound book; a web page has no spine, so honouring it literally
  // just makes the text block off-centre. It would also move every verse number: `.marginpar`
  // floats itself 11mm to the LEFT of the text (6mm width + 5mm), and the body's left padding is
  // what supplies that room. Three coupled decisions — asymmetry on a spineless medium, the
  // gutter, and verse-number placement — so it needs an owner ruling and its own commit.
  String.raw`  --latex-margin-left: ${marginLeft};
  --latex-margin-right: ${marginRight};
` +
  // PREAMBLE LINK: \setlength{\parskip}{3pt plus 3pt minus 2pt} (preambuleV3.tex:123),
  // \setlength{\parindent}{25pt} (:158) and \setlength{\parsep}{2pt plus 1pt minus 1pt} (:131).
  // All three READ AND HONOURED — settings.ts R-029/R-030/R-031. Consumed by `p { margin }`,
  // `p { text-indent }` and `li { margin }` below. /!\ The rubber part (plus/minus) is discarded,
  // and pt→px keeps the raw JS float: 25pt lands as 33.33333333333333px.
  String.raw`  --latex-par-skip: ${parSkip};
  --latex-par-indent: ${parIndent};
  --latex-parsep: ${parSep};
` +
  // PREAMBLE LINK: \linespread{1.1} (preambuleV3.tex:136). READ AND HONOURED — settings.ts R-032,
  // then multiplied by 1.2 here (LaTeX's own baseline factor), so 1.1 becomes 1.32 and is applied
  // by `body { line-height }`.
  String.raw`  --latex-line-height: ${lineHeight};
` +
  // PREAMBLE LINK: \setlength{\marginparwidth}{6mm} (preambuleV3.tex:174). READ AND HONOURED —
  // settings.ts R-037. This is the verse-number column: consumed by .marginpar (and by the dead
  // .nverset/.npar/.nhidden) below. Serves \Nverset, \Npar and \Nhidden, which the preamble
  // builds on \marginpar. /!\ Nothing sets box-sizing, so the 6mm only lands correctly because
  // the arithmetic happens to work out (DF-11).
  String.raw`  --latex-marginpar-width: ${marginParWidth};
` +
  // PREAMBLE LINK: \setlength{\leftmargini}{25pt} (preambuleV3.tex:166). READ AND HONOURED —
  // settings.ts R-038. Consumed by the blockquote rule near the end of this stylesheet, which is
  // what the preamble's \citation → \begin{quote} expansion produces.
  String.raw`  --latex-quote-margin: ${leftMarginQuote};
` +
  // PREAMBLE LINK: NOT \sloppy (preambuleV3.tex:184) — see the note on `const textAlign` above.
  // This variable has exactly one consumer, the `.extract` rule below, and no code anywhere emits
  // class="extract", so the whole chain styles nothing today.
  String.raw`  --latex-text-align: ${textAlign};
` +
  // PREAMBLE LINK: \FBsetspaces{colon}{0.5}{0.1}{0.1} and \FBsetspaces{thin}{…}
  // (preambuleV3.tex:142-143). PARSED, THEN DEAD. settings.ts R-035/R-036 reads the first of the
  // three arguments, halves it here — and no rule in this stylesheet ever writes
  // var(--french-colon-space) or var(--french-thin-space) (RK-38). The French space before : ; ! ?
  // is therefore NOT applied; the only spacing that survives is the &nbsp; that R-222 makes from a
  // `~` a translator typed by hand. The shipped comment on the next line says the same thing to
  // the reader of the stylesheet; "see note above" in it refers to a header block that no longer
  // exists — leave the text alone, it is part of the emitted bytes.
  String.raw`  /* French spacing variables - NOT ACTIVELY USED (see note above)
   * To enable, implement processFrenchPunctuation() to wrap punctuation in spans */
  --french-colon-space: ${colonSpace};
  --french-thin-space: ${thinSpace};
` +
  // PREAMBLE LINK: NONE — and that is the point. `preambuleV3.tex` contains no `\documentclass`;
  // the body size comes from the DOCUMENT, which in all eight books is `\documentclass[12pt]{article}`.
  // The preamble is silent here, so this is a LaTeX-default question — see docs/LaTeXDefaults.md.
  //
  // ONE number governs the whole type scale. Before 2026-09-02 the body was a bare
  // `font-size: 12pt` and the headings were sized in `rem`, i.e. against the BROWSER ROOT (16px),
  // completely independent of it — `h1` came out at 24pt where LaTeX's `\section` is `\Large` =
  // 17.28pt at a 12pt base.
  //
  // READ FROM THE DOCUMENT since 2026-09-02 (settings.ts R-352): `\documentclass[11pt]` now
  // renders at 11pt, and the headings and heading skips follow it. The `|| '12pt'` fallback is
  // NOT LaTeX's default — it is reached only when the input has no `\documentclass` at all, which
  // is not a compilable LaTeX document and therefore has no LaTeX default to match. A document
  // that DOES declare a class but no size option gets LaTeX's real default, 10pt, decided in
  // settings.ts where the three cases are set out.
  //
  // /!\ STILL NOT EXACT AT A SIZE OTHER THAN 12pt. LaTeX's size steps are a per-class lookup
  // table, not a fixed ratio: at a 14pt base `\Large` is 20.74pt where `1.44em` gives 20.16pt.
  // The proportions hold; the absolute values drift a few percent. Encoding `article`'s three
  // tables is what would fix it. Recorded in docs/LaTeXDefaults.md.
  String.raw`  --latex-body-size: ${settings.bodyFontSize || '12pt'};
}

body {
  font-family: var(--latex-main-font);
  font-size: var(--latex-body-size);
  line-height: var(--latex-line-height);
  color: var(--latex-text);
  background: var(--latex-bg);
` +
  // PREAMBLE LINK: \geometry{paper=a4paper,total={160mm,247mm},inner=25mm,top=25mm}
  // (preambuleV3.tex:107). These three lines are the ONLY place the requested page geometry
  // reaches the output. Since 2026-09-07 two of the three are right: `max-width` is the requested
  // width (160mm with V3, not the old 172mm constant) and `margin-top` is the requested `top`.
  // STILL NOT EXPRESSED: the left/right padding is symmetric, so `inner=25mm` has no effect
  // (DF-11 — see the note on --latex-margin-left above for why connecting it is its own decision).
  // Height and bottom margin have no home at all, and deliberately so: there is no @page rule
  // because the target is the web, not paper (RK-34).
  String.raw`  max-width: var(--latex-page-width);
  margin: var(--latex-margin-top) auto;
  padding: 0 var(--latex-margin-right);
` +
  // PREAMBLE LINK: \usepackage[all]{nowidow} (preambuleV3.tex:29). HARD-CODED — that line is NEVER
  // parsed (R-143 deletes every \usepackage, and settings.ts has no rule for it). The two
  // declarations below satisfy the request by hand, and are repeated in the @media print block at
  // the end of this file. The output is right; the causal link is absent. Editing or deleting the
  // preamble line changes nothing here, so do not read the correct result as proof it is read.
  String.raw`  /* Widow and orphan control */
  widows: 3;
  orphans: 3;
}

/* Typography */
sup, sub {
  font-size: 0.7em;
  line-height: 0;
  position: relative;
  vertical-align: baseline;
}
sup { top: -0.4em; }
sup { top: -0.4em; }
sub { bottom: -0.2em; }

` +
  // PREAMBLE LINK: \renewcommand{\makeenmark}{\hspace{0.1em}\textsuperscript{\theenmark}}
  // (preambuleV3.tex:465). PARTLY READ. settings.ts R-045/R-047 pulls the \hspace length out of
  // that definition and it becomes the .endnote-num margin below — this is the only preamble line
  // whose value reaches a note. The \textsuperscript half is NOT read: notes.ts hard-codes the
  // <sup>. There is no \makefnmark in the preamble, so .footnote-num's margin is always the '0'
  // fallback. .endnote-num became live on 2026-08-29 when the DF-1 fix put the notes block back
  // on the page; before that both rules were inert.
  String.raw`/* Footnote and Endnote markers */
.footnote-num {
  margin-left: ${settings.footnoteSpacing || '0'};
}
.endnote-num {
  margin-left: ${settings.endnoteSpacing || '0'};
}

` +
  // THE HEADING TYPE SCALE IS DERIVED FROM LaTeX, NOT CHOSEN. Rebuilt 2026-09-02 after the owner
  // reported the gap between two consecutive `\section*` reading far larger on the web than in the
  // PDF. It measured 43.2pt against LaTeX's ~18.1pt — 2.4× — from three compounding causes:
  //
  //   1. `h1` was `2rem`. `rem` is the BROWSER ROOT (16px = 24pt), not this document's body, so the
  //      heading scale ignored the 12pt body entirely and `\section` came out at 24pt where LaTeX
  //      gives `\Large` = 17.28pt.
  //   2. `margin-top: 1.5em` resolves against the heading's OWN size, so that 39% oversize
  //      compounded into the spacing: 1.5em of 24pt = 36pt.
  //   3. `padding-bottom: 0.3em` — 7.2pt with no LaTeX equivalent at all. A web flourish, normally
  //      paired with a `border-bottom` that was never there. Removed.
  //
  // `article` at a 12pt base, which is what all eight books declare:
  //
  //   \section       \Large       17.28pt  ⇒  1.44em     beforeskip 3.5ex ≈ 18.1pt  ⇒  ×1.51
  //   \subsection    \large       14.40pt  ⇒  1.2em      afterskip  2.3ex ≈ 11.9pt  ⇒  ×0.99
  //   \subsubsection \normalsize  12.00pt  ⇒  1em
  //
  // /!\ THE SKIPS ARE `calc()` OF THE BODY SIZE, NOT `em`, AND THAT IS THE WHOLE POINT. LaTeX
  // measures beforeskip/afterskip in the BODY font, so the space above a heading is the same at
  // every level. CSS `em` measures against the heading's own font-size, which makes the gap grow
  // with the heading — the modelling error behind cause 2. Anchoring to --latex-body-size restores
  // LaTeX's model and keeps one number in charge of the whole scale.
  //
  // /!\ 1ex ≈ 0.43em is a FONT METRIC, so 18.1/11.9 are approximate to a few percent for
  // Merriweather. The ratios between the three gaps are exact; the absolute values are not.
  //
  // /!\ AT A BODY SIZE OTHER THAN 12pt THE PROPORTIONS HOLD BUT LaTeX IS NOT MATCHED EXACTLY.
  // LaTeX's size steps are a per-class lookup table, not a fixed ratio: at a 14pt base `\Large` is
  // 20.74pt, where 1.44em gives 20.16pt (3% low). Encoding `article`'s three tables would fix that
  // and is not worth it while every book is [12pt].
  String.raw`
h1, h2, h3, h4, h5, h6 {
  font-weight: 600;
  line-height: 1.3;
  margin-top: calc(var(--latex-body-size) * 1.51);
  margin-bottom: calc(var(--latex-body-size) * 0.99);
}

h1 { font-size: 1.44em; }
h2 { font-size: 1.2em; }
h3 { font-size: 1em; }
h4 { font-size: 1em; }

` +
  // PREAMBLE LINK: three separate preamble lines land in this one rule, and only one of them is
  // actually read.
  //   • margin      ← \setlength{\parskip} (preambuleV3.tex:123) — READ AND HONOURED.
  //   • text-indent ← \setlength{\parindent}{25pt} (:158)        — READ AND HONOURED.
  //   • text-align  ← \sloppy (:184) is NOT the source of this. Nothing derives justification from
  //     the preamble at all; it is hard-coded here, and the !important means no preamble value
  //     could override it even if one existed. \sloppy itself has no CSS equivalent and needs none
  //     — a browser always stretches spaces on a justified line.
  //   • hyphens     ← \usepackage[none]{hyphenat} (:24) asks for NO hyphenation. It IS parsed, into
  //     settings.noHyphenation, and then nothing reads it: these two declarations are emitted
  //     unconditionally and say the exact opposite of what the preamble asked for (DF-12 / RK-35).
  //     Fixing DF-12 means making these two conditional here, not changing the preamble.
  String.raw`p {
  margin: var(--latex-par-skip) 0;
  text-align: justify !important;
  text-indent: var(--latex-par-indent);
  hyphens: auto;
  -webkit-hyphens: auto;
}

/* Ensure all text content is justified */
.latex-document, .latex-document * {
  text-align: justify;
}
.latex-document .title, .latex-document .author, .latex-document .date,
.latex-document .abstract-title, .latex-document .ntablet {
  text-align: center;
}

.latex-document h1, .latex-document h2, .latex-document h3, .latex-document h4 {
  text-align: left;
}

p:first-child {
  text-indent: 0;
}

/* Allow centering in specific containers to override p justification */
div[style*="text-align: center"] p,
.center-env p,
.figure-env p,
.comtrad p,
.date p {
  text-align: center !important;
  text-indent: 0 !important;
}

/* Environment Styles */
.center-env { text-align: center; }
.flushleft-env { text-align: left; }
.flushright-env { text-align: right; }
.figure-env { text-align: center; margin: 1.5em 0; }

/* Title block */
.title {
  text-align: center !important;
  font-size: 1.7em; /* LaTeX LARGE size */
  font-weight: 700;
  margin: 1em 0 0.5em;
  border: none;
  text-indent: 0 !important;
}

.author {
  text-align: center !important;
  font-size: 1.1em; /* LaTeX default author size */
  margin: 0.5em 0;
  text-indent: 0 !important;
}

.date {
  text-align: center !important;
  font-size: 1em;
  color: #555;
  margin: 0.5em 0 1.5em;
  text-indent: 0 !important;
}

/* Abstract */
.abstract {
  margin: 1.5em 3em;
  font-size: 0.95em;
}

.abstract-title {
  text-align: center;
  font-weight: 600;
  margin-bottom: 0.5em;
}

/* Lists */
ul, ol {
  margin: 0.75em 0;
  padding-left: 2em;
}

` +
  // PREAMBLE LINK: \setlength{\parsep}{2pt plus 1pt minus 1pt} (preambuleV3.tex:131). READ AND
  // HONOURED — this is its only consumer. The preamble comment calls it the space between two
  // items of an itemize or enumerate, and that is exactly what it does here. The list
  // ENVIRONMENTS themselves come from \usepackage{enumitem} (:57), which is not read — see the
  // note on findInnermostListEnv in environments.ts.
  String.raw`li { 
  margin: var(--latex-parsep, 0.25em) 0;
}

dl { margin: 0.75em 0; }
dt { font-weight: 600; margin-top: 0.5em; }
dd { margin-left: 2em; }


/* Code */
code {
  font-family: var(--latex-mono-font);
  font-size: 0.9em;
  background: var(--latex-code-bg);
  padding: 0.15em 0.4em;
  border-radius: 3px;
}

pre {
  font-family: var(--latex-mono-font);
  font-size: 0.85em;
  background: var(--latex-code-bg);
  padding: 1em;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1em 0;
}

pre code {
  background: none;
  padding: 0;
}

/* Tables */
table {
  border-collapse: collapse;
  margin: 1em auto;
  width: auto;
}

th, td {
  padding: 0.5em 1em;
}

/* Grid tables (with vertical rules in LaTeX format) */
.grid-table th,
.grid-table td,
.longtable.grid-table th,
.longtable.grid-table td {
  border: 1px solid var(--latex-border);
}

.grid-table th,
.longtable.grid-table th {
  background: var(--latex-code-bg);
  font-weight: 600;
}

/* Plain LaTeX tables (no vertical rules) have no visible grid */
.plain-table th,
.plain-table td,
.longtable.plain-table th,
.longtable.plain-table td {
  border: none;
}

/* Alignment helpers based on column spec */
.tabular-align-l { text-align: left; }
.tabular-align-c { text-align: center; }
.tabular-align-r { text-align: right; }

td.tabular-align-r {
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
}

` +
  // PREAMBLE LINK: \hfil (the house centring idiom — ten preamble macros use it: \Ntablet :257,
  // \glad :434, \arbsq :387, seven \appelC*) and \centering. Stamped by processSections in
  // commands-text.ts, which carries the full note; the preamble carries its French half at the
  // head of "Commandes de formats" ("DÉCISION DU 2026-08-31"). Analysis:
  // docs/HFIL-AND-CENTRING.md. Catalogued as a deliberate substitution in
  // docs/PREAMBLE-NOT-HONOURED.md §E1.
  //
  // ONE CLASS, TWO COMMANDS, AND ONLY ONE OF THEM IS EXACTLY REPRODUCED.
  //   \centering → this rule is CORRECT. \centering centres every line in LaTeX; text-align:
  //                center centres every line in CSS. An exact pair. Nothing to fix.
  //   \hfil      → this rule is a SUBSTITUTION, decided 2026-08-31. \hfil is stretchable space,
  //                not a centring command; it centres only while the text fits on one line. Beyond
  //                that XeLaTeX gives first line flush right, middles justified, last flush left,
  //                while this rule keeps centring everything. 31 of the 32 \hfil headings in the
  //                corpus fit on one line and are unaffected.
  //
  // DO NOT "fix" \hfil by editing this rule — it would break \centering, which shares the class.
  // The split belongs in processSections, where the class is chosen: once CSS runs, both commands
  // have become the same class and the distinction no longer exists. A working exact reproduction
  // (width:fit-content + margin-inline:auto, matching one and two lines by LaTeX's own mechanism)
  // is written out in docs/HFIL-AND-CENTRING.md §5.1 and was deliberately declined.
  String.raw`.centered-heading {
  text-align: center !important;
  text-indent: 0 !important;
}

/* Figures */
figure {
  text-align: center;
  margin: 1.5em 0;
}

figcaption {
  font-size: 0.9em;
  color: #555;
  margin-top: 0.5em;
}

/* Math */
.math-display {
  display: block;
  text-align: center;
  margin: 1.5em 0;
  overflow-x: auto;
}

.math-inline {
  display: inline;
}

.math-error {
  color: #cc0000;
  font-family: monospace;
}

/* Footnotes and Endnotes */
.footnote, .endnote {
  cursor: help;
  color: #ad9160;
  text-decoration: none;
}

/* LaTeX/TeX logos */
.latex-logo sup, .latex-logo sub,
.tex-logo sup, .tex-logo sub {
  font-size: 0.7em;
}

.latex-logo sup { vertical-align: 0.5em; margin-left: -0.3em; margin-right: -0.15em; }
.latex-logo sub { vertical-align: -0.3em; margin-left: -0.2em; }
.tex-logo sub { vertical-align: -0.3em; margin-left: -0.15em; }

/* Vertical spacing */
.bigskip { margin: 1.5em 0; }
.medskip { margin: 1em 0; }
.smallskip { margin: 0.5em 0; }
.vfill { flex-grow: 1; min-height: 3em; }

/* Horizontal spacing */
/* Make paragraphs with hfil/hfill use flexbox so flex-grow works */
p:has(.hfil), p:has(.hfill) {
  display: flex;
  flex-wrap: wrap;
  text-indent: 0 !important;
}
.hfill { flex-grow: 1; display: inline-block; }
.hfil { flex-grow: 1; display: inline-block; } /* In flexbox context, hfil before content centers it */

/* No indent for specific paragraphs */
.noindent + p, p:has(.noindent) {
  text-indent: 0 !important;
}

/* Center line - for the \noindent\hfil pattern */
p:has(.center-line) {
  text-align: center !important;
  text-indent: 0 !important;
}
.center-line { display: none; } /* Marker only, no visual output */

` +
  // PREAMBLE LINK: \reversemarginpar (preambuleV3.tex:176) and \setlength{\marginparwidth}{6mm}
  // (:174) — the pair the preamble says "commande toutes les commandes bâties sur \marginpar:
  // \Nverset, \Npar et \Nhidden".
  //   • the WIDTH is read (settings.ts R-037 → --latex-marginpar-width, used just below);
  //   • the LEFT PLACEMENT is NOT. \reversemarginpar is parsed by nothing and merely deleted from
  //     the body (notes.ts removeReverseMarginpar, R-268). `float: left` plus the negative
  //     margin-left below are hard-coded, and they are the only reason verse numbers land in the
  //     left margin at all.
  // Do NOT "implement" \reversemarginpar: left placement is a fixed editorial requirement that
  // this rule already satisfies. This is also the rule that carries production, because in the
  // live path \Nverset expands to \marginpar and lands on THIS class — never on .nverset (RK-37).
  // /!\ Nothing declares box-sizing, so the 6mm column only fits by arithmetic coincidence (DF-11).
  String.raw`/* Margin notes */
.marginpar {
  float: left;
  width: var(--latex-marginpar-width);
  margin-left: calc(-1 * var(--latex-marginpar-width) - 5mm);
  font-size: 0.75em;
  color: #666;
  text-align: right;
  padding-right: 5mm;
}

/* Font sizes */
.tiny-text { font-size: 0.5em !important; }
.scriptsize { font-size: 0.7em !important; }
.footnotesize { font-size: 0.8em !important; }
.small-text { font-size: 0.875em !important; }
.large-text { font-size: 1.2em !important; }
.Large-text { font-size: 1.4em !important; }
.LARGE-text { font-size: 1.7em !important; }
.huge-text { font-size: 2em !important; }
.Huge-text { font-size: 2.5em !important; }
` +
  // PREAMBLE LINK: \textsc — the preamble's own small-caps request, in \ltrPar / \ltrNum
  // (preambuleV3.tex:500 / :502): "lettres suivantes en petites capitales (sc)". This class is
  // what a STANDALONE \textsc lands on (R-156); the lettrine's tail has its own .ltr-rest below.
  //
  // WHY THERE IS A font-size HERE AT ALL. \textsc does not change size in LaTeX — it swaps in a
  // designed small-caps face whose small capitals sit at roughly 80% of cap height but at FULL
  // stroke weight. Merriweather as loaded from Google Fonts carries no `smcp` glyphs, so the
  // browser SYNTHESISES small caps by scaling capitals down, which also thins them. The result
  // reads markedly smaller and lighter than the PDF, and the initial capital of a line then
  // towers over the rest — the owner reported exactly that on
  // `\appelCRS{\textsc{À réciter chaque jour le matin, à midi et le soir.}}`.
  //
  // /!\ THE NUMBER IS A JUDGEMENT, NOT A DERIVATION. Unlike the lettrine's 1.73em — which comes
  // from LaTeX's type scale, \LARGE 20.74pt ÷ 12pt — there is no ratio to compute here, because
  // \textsc changes no size. 1.24em is the owner's value, set 2026-08-31. It is deliberately
  // LARGER than .ltr-rest's 1.12em: that one sits beside a 1.73em drop cap, this one stands alone
  // on a centred line. Tune it HERE, never in the converter. Recorded in
  // docs/PREAMBLE-NOT-HONOURED.md §E2.
  //
  // /!\ `em` compounds. \textsc inside another sized box multiplies — inside .marginpar (0.75em)
  // a small-caps run would land at 0.93em, not 1.24em. No corpus document nests them today.
  //
  // THE ROOT FIX, not taken: a face that actually carries small-caps glyphs. Then both this
  // number and .ltr-rest's 1.12em could go, and nothing would be a judgement call. It means
  // changing or extending the typeface, so it is recorded rather than done.
  //
  // NOT the call word's business. \appelCRS is Romain — it applies no font change (its body is
  // `\par\noindent\hfil{#1}\nopagebreak\medskip`), and `p:has(.center-line)` contributes only
  // text-align and text-indent. A rule keyed on "\appelCRS followed by \textsc" was considered
  // and rejected: it would invent a distinction LaTeX does not make and leave \appelCI, \appelSCI
  // and every inline \textsc still wrong.
  String.raw`
/* Small caps */
.small-caps { font-variant: small-caps; font-size: 1.24em; }

/* Text underbar (for transliteration like 	s{h}) */
.text-underbar { 
  text-decoration: underline;
  text-decoration-style: solid;
}

` +
  // PREAMBLE LINK: the whole "Commandes de formats" section of preambuleV3.tex (:208-449) —
  // \Tital (:245), \Ntablet (:254), \appel* (:314-379), \arbsq (:384), \ltrPar/\ltrNum (:394-396),
  // \ts (:404), \KAnote/\KAquestion/\KAreponse (:413-425), \glad (:431), \Rextrait/\Nextrait
  // (:441-448). Every class from here down to the Kitáb-i-Aqdas block below is DEAD ON THE LIVE
  // PATH (RK-37 / DF-15): production concatenates the preamble ahead of the document, so S7
  // expands each of those macros into generic LaTeX before the hand-written handlers in
  // commands-domain.ts can fire. A \Tital therefore emits <em> inside a <p>, never .tital.
  // /!\ CONDITIONAL, NOT ABSOLUTE — clear the preamble box and Mode B applies: commands-domain.ts
  // fires for real and these selectors match. Do not delete them as unused.
  String.raw`/* Custom Bahá'í text styles */
.tital {
  margin-top: 1.5em;
  text-indent: 0;
}
.ntablet {
  text-align: center;
  margin: 1.5em 0;
  text-indent: 0;
}
.appel {
  text-indent: 0;
}

` +
  // PREAMBLE LINK: the twelve \appel* call-word macros (preambuleV3.tex:314-379). EMITTED BY
  // NOTHING on the live path, and the consequence is bigger than a missing class: in Mode A a call
  // word is not a paragraph of its own at all — it is inserted inline, so `text-indent: 0` and the
  // margins below never apply to it. Verified on real output 2026-08-15.
  //   • \appelGB → <strong>Mot</strong>, \appelCI → <span class="center-line"></span><em>Mot</em>.
  //   • Centring works, but through `p:has(.center-line)` earlier in this file, not through
  //     .appel-c*. If the translator omits the blank line after the call word, that rule centres
  //     the FOLLOWING body text too, because both share one <p>.
  //   • .appel-cbl / .appel-crls promise a larger size that never arrives: \Large is stripped at
  //     S11 slot 9 before it can mean anything.
  // These selectors are alive in Mode B (empty preamble box) — do not delete them.
  String.raw`/* Mots d'appel variants */
.appel-gi {
  text-align: left;
  font-style: italic;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-sgi {
  text-align: left;
  font-style: italic;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-gb {
  text-align: left;
  font-weight: bold;
  margin-top: 0;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-gr {
  text-align: left;
  margin-top: 0;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-sgr {
  text-align: left;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-ci {
  text-align: center;
  font-style: italic;
  margin-top: 0;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-sci {
  text-align: center;
  font-style: italic;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-cbl {
  text-align: center;
  font-weight: bold;
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 0.5em;
  text-indent: 0;
}

.appel-crs {
  text-align: center;
  margin-top: 0;
  margin-bottom: 1em;
  text-indent: 0;
}

.appel-crls {
  text-align: center;
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 1.5em;
  text-indent: 0;
}

` +
  // LETTRINE — the rule used to be declared HERE **and** again in "Visual Separators" below.
  // Same specificity, so the two MERGED property by property: `font-weight` came from this
  // block, `font-size` and `float` from the other. Editing either one alone produced a
  // half-effect, which is exactly the trap it looks like. **Consolidated 2026-08-31** into the
  // later block, where the full rule and its PREAMBLE LINK note now live. Nothing is declared
  // here any more — do not reintroduce a second declaration.
  String.raw`` +
  // PREAMBLE LINK: \Nverset (preambuleV3.tex:264) — the numbered verse of the Writings. This is
  // the SECOND declaration of .marginpar (the first is above, at 0.75em); this one wins the
  // cascade and sets the live font-size to 0.7em, which is the \scriptsize the preamble asks for.
  // .nverset itself is emitted by nothing on the live path — Mode A rewrites \Nverset into
  // \marginpar long before commands-domain.ts could emit the domain class (RK-37).
  // Do not dedupe the two .marginpar blocks: the second is the one that carries production.
  String.raw`.nverset,
.marginpar {
  float: left;
  width: var(--latex-marginpar-width);
  margin-left: calc(-1 * var(--latex-marginpar-width) - 5mm);
  font-size: 0.7em;
  color: #666;
  text-align: right;
  padding-right: 5mm;
}
` +
  // PREAMBLE LINK: \Rextrait / \Nextrait (preambuleV3.tex:441, :270) — the selected-extract pair
  // used in the compilations and the ASN reference documents. EMITTED BY NOTHING: no code anywhere
  // in src/ writes class="extract", "extract-ref" or "extract-num" (checked by bare-identifier
  // search, not by match count). The two macros expand to a bare \begin{quote}…\end{quote}, so a
  // real extract is styled by the blockquote rule near the end of this file instead.
  // /!\ .extract is also the ONLY consumer of --latex-text-align, so that variable — and with it
  // the \raggedright detection in settings.ts — currently reaches nothing at all.
  // The extract NUMBER used to be separately broken (DF-2): \Nextrait is defined twice under
  // \ifpandoc and the first (Pandoc) definition won, publishing a literal \verb|<TAB>| in front
  // of the number. FIXED 2026-08-29 — S1b keeps the \else branch, whose \hfill lands the number
  // flush right via the .hfill rule.
  String.raw`.extract {
  margin: 1em var(--latex-quote-margin);
  text-align: var(--latex-text-align);
}
.extract-ref {
  display: block;
}
.extract-num {
  display: block;
  text-align: right;
  margin-top: 0.5em;
}
` +
  // PREAMBLE LINK: \afficherComTrad (preambuleV3.tex:518) — the translators' credit at the end of
  // the book. EMITTED BY NOTHING: the macro expands to \newpage\vfill\begin{center}…\end{center},
  // so the credit really is published, but it lands in .center-env. Nothing writes
  // class="comtrad" or class="version-info". The `.comtrad p` centring rule earlier in this file
  // is dead for the same reason.
  String.raw`.comtrad {
  text-align: center;
  margin-top: 3em;
  padding-top: 3em;
}
.version-info {
  font-size: 0.85em;
  color: #666;
}
` +
  // PREAMBLE LINK: \usepackage{endnotes} (preambuleV3.tex:40) and \afficherEndNotes (:487, :492) —
  // the translator's notes at the end of the book. This SELECTOR is still emitted by nothing:
  // notes.ts writes class="footnotes endnotes", never "endnotes-section", so the rule below is
  // dead CSS and always was. What is no longer true is the reason it mattered: until 2026-08-29
  // the notes block itself never reached the page at all (DF-1), because \afficherEndNotes
  // expanded to the empty string. S1b now resolves \ifpandoc to the \else branch and the block is
  // inserted — styled by .footnotes/.endnotes below, not by this selector.
  String.raw`.endnotes-section {
  margin-top: 2em;
  padding-top: 1em;
  border-top: 1px solid var(--latex-border);
}

` +
  // PREAMBLE LINK: \arbsq (preambuleV3.tex:387), \ltrPar (:422) / \ltrNum (:424), \glad (:434).
  //
  // .ltr-initial / .ltr-rest — LIVE SINCE 2026-08-31, and this is now their ONLY declaration.
  // The preamble asks for *"Lettrine (ltr): première lettre en taille LARGE et lettres suivantes
  // en petites capitales (sc)"*. Both classes existed from the port and were EMITTED BY NOTHING:
  // the converter understood only `\LARGE{X}`, while the preamble writes `{\LARGE X}` — LaTeX's
  // real construct, a group with a size switch inside it. R-202 deleted the bare `\LARGE` and the
  // group's braces were published as literal text, so `\ltrPar{Ô}{ peuples}` reached the page as
  // `{ Ô}` followed by ordinary small caps. R-156b (`processLettrine`, commands-text.ts slot 3)
  // now recognises the pair and emits these classes.
  //
  // .ltr-initial IS DERIVED, NOT GUESSED — settled 2026-08-31 against the real PDF.
  //   `body { font-size: 12pt }`, and at a 12pt base LaTeX's size steps are
  //   \normalsize 12 · \large 14.4 · \Large 17.28 · \LARGE 20.74 · \huge 24.88 (pt).
  //   **\LARGE ÷ \normalsize = 20.74 / 12 = 1.728**, hence `font-size: 1.73em`.
  //   `line-height: 0.76` keeps the first line's box at body leading: 1.73 × 0.76 = 1.315em,
  //   against the 1.32em that `\linespread{1.1}` gives. So the cap sits ON the first line and
  //   nothing below it moves.
  //   No `font-weight`: the cap inherits the body weight, as it does in the PDF.
  //   No `margin-right`: the gap comes from the translator's own `\ltrPar{Ô}{ peuples}`, whose
  //   second argument begins with a space.
  //
  // /!\ TWO EARLIER ATTEMPTS, BOTH WRONG, BOTH REVERTED THE SAME DAY — do not repeat either:
  //   1. `float: left` + 3.4em. A floated cap makes the next two or three lines wrap around it
  //      and displaces the paragraph. `\LARGE` does not float; it enlarges the letter in place.
  //   2. 3.2em with `line-height: 0.42`. Right about not floating, still far too large — it was
  //      chosen by eye, and eye was not needed. **The PDF is the output of `{\LARGE Ô}`, so the
  //      ratio is knowable exactly.** Measuring a screenshot would have been guessing at a
  //      number the type scale already gives.
  //
  // .ltr-rest keeps a small bump (1.12em). `\textsc` does not change size in LaTeX, but CSS
  // `font-variant: small-caps` renders visibly smaller than LaTeX's real small caps, and the
  // owner asked for "slightly larger". That one IS a judgement — tune it HERE, never in the
  // converter. Recorded in docs/PREAMBLE-NOT-HONOURED.md §E2.
  //
  // /!\ \arbsq is still worse than merely unstyled: it expands to \textasciitilde, R-211 turns
  // that into `~`, and R-222 then turns every `~` into &nbsp; — so the arabesque publishes as an
  // invisible blank line rather than a separator (DF-14, still open). The fix belongs in
  // commands-text.ts, not here; this rule would style it correctly if anything emitted the class.
  String.raw`/* Visual Separators */
.arbsq {
  text-align: center;
  margin: 1.5em 0;
}
.ltr-initial {
  font-size: 1.73em;
  line-height: 0.76;
}
.ltr-rest {
  font-variant: small-caps;
  font-size: 1.12em;
  letter-spacing: 0.02em;
}
.glad {
  text-align: center;
  font-weight: bold;
  margin: 1em 0;
}

/* Mots d'appel */
/* NOTE: The base \appel command is not explicitly defined in the preamble,
   but the PDF output shows it renders as italic. Adding italic as default
   until the original \appel definition is located. */
.appel { text-indent: 0; margin: 0.5em 0; font-style: italic; }
.appel-gi { text-align: left; font-style: italic; text-indent: 0; margin: 0.5em 0; }
.appel-sgi { text-align: left; font-style: italic; text-indent: 0; margin: 1.5em 0 0.5em; }
.appel-gb { text-align: left; font-weight: bold; text-indent: 0; margin: 0.5em 0; }
.appel-gr { text-align: left; font-style: normal; text-indent: 0; margin: 0.5em 0; }
.appel-sgr { text-align: left; font-style: normal; text-indent: 0; margin: 1.5em 0 0.5em; }
.appel-ci { text-align: center; font-style: italic; text-indent: 0; margin: 0.5em 0; }
.appel-sci { text-align: center; font-style: italic; text-indent: 0; margin: 1.5em 0 0.5em; }
.appel-cbl { text-align: center; font-weight: bold; font-size: 1.4em; text-indent: 0; margin: 0.5em 0; }
.appel-crs { text-align: center; font-style: normal; text-indent: 0; margin: 0.5em 0 1em; }
.appel-crls { text-align: center; font-style: normal; font-size: 1.4em; text-indent: 0; margin: 0.5em 0 1.5em; }

` +
  // PREAMBLE LINK: \Npar (preambuleV3.tex:278) and \Nhidden (:289). Both build on \marginpar with
  // the widths set at :126-128, and both sizes below match what the preamble asks for (\tiny for
  // \Npar, \scriptsize plus \bigskip for \Nhidden). EMITTED BY NOTHING on the live path: Mode A
  // routes all three numbering macros through .marginpar above, where \Npar keeps `tiny-text` but
  // \Nhidden loses its size class altogether. Owner ruling 2026-08-14: \Npar numbers paragraphs
  // but is treated exactly as a verse.
  String.raw`/* Marginal Notes */
.npar {
  float: left;
  width: var(--latex-marginpar-width);
  margin-left: calc(-1 * var(--latex-marginpar-width) - 5mm);
  font-size: 0.6em; /* tiny */
  color: #666;
  text-align: right;
  padding-right: 5mm;
}
.nhidden {
  float: left;
  width: var(--latex-marginpar-width);
  margin-left: calc(-1 * var(--latex-marginpar-width) - 5mm);
  font-size: 0.7em; /* scriptsize */
  color: #666;
  text-align: right;
  padding-right: 5mm;
  margin-top: 1.5em; /* bigskip */
}

` +
  // PREAMBLE LINK: \KAnote, \KAquestion, \KAreponse (preambuleV3.tex:413-425) — the Kitáb-i-Aqdas
  // note / question / answer headings. EMITTED BY NOTHING on the live path, same Mode-A expansion
  // as the block above: \KAquestion publishes as <em><strong>Question</strong>&nbsp;: {…}</em>,
  // with the literal braces of the preamble's `{#1}` still in the text (R-348).
  String.raw`/* Kitáb-i-Aqdas */
.ka-note { font-weight: bold; margin: 1em 0 0; text-indent: 0; }
.ka-question { font-style: italic; font-weight: bold; margin: 0.5em 0; }
.ka-reponse { font-style: italic; margin: 0.5em 0; }

/* Footnotes */
.footnote-num {
  color: #000;
  cursor: pointer;
  font-size: 0.65em;
  vertical-align: super;
  line-height: 0;
  position: relative;
  top: -0.3em;
}
.footnote-separator {
  margin-top: 3em;
  margin-bottom: 3em;
  border: none;
  border-top: 1px solid var(--latex-border);
  width: 30%;
}
.footnotes {
  font-size: 0.9em;
  margin-top: 1em;
}
.footnote-item {
  margin: 0.5em 0;
  text-indent: 0;
  color: #000;
}
.footnote-item sup {
  margin-right: 0.5em;
  color: #000;
}
.endnote-num {
  font-weight: normal;
  color: #000;
}
.endnotes {
  text-indent: 0;
  color: #000;
}
.footnote-marker {
  color: #000;
}
.note-link, .note-backlink {
  text-decoration: none;
  color: #ad9160;
}
.note-link:hover, .note-backlink:hover {
  text-decoration: underline;
}
.note-backlink span {
  color: #000;
}

` +
  // PAGE BREAK ORNAMENT — a JS comment, deliberately outside the template. A comment
  // written INSIDE this template is a CSS comment and ships to translators in
  // `latex-styles.css` (see the header note on why the template is split at all).
  //
  // `.page-break` used to draw `border-top: 1px dashed` AS WELL AS an `<img>` that pointed at
  // an asset this repo never contained, so a reader saw a dashed rule with a broken-image icon
  // on it. Both are gone: the inline SVG fleuron in commands-text.ts (`PAGE_BREAK_ORNAMENT`)
  // is the divider, and a rule drawn behind it fought it.
  //
  // `color` feeds the SVG's `currentColor`, so the ornament carries no fill of its own and
  // works in both themes and in print. `--latex-ornament` is `#ad9160`, the same gold the
  // note links use — an ornament is not a border and should not track the border token.
  // `line-height: 0` stops the inline SVG inheriting text leading it does not need.
  String.raw`/* Page breaks */
.page-break {
  page-break-after: always;
  margin: 2em 0;
  text-align: center;
  line-height: 0;
}
.page-break-ornament {
  display: inline-block;
  max-width: 100%;
  height: auto;
  color: var(--latex-ornament);
}

/* Table of contents placeholder */
.toc-placeholder, .endnotes-placeholder {
  padding: 2em;
  text-align: center;
  color: #999;
  font-style: italic;
  border: 1px dashed var(--latex-border);
  margin: 1em 0;
}

/* Table of Contents Styling */
.table-of-contents {
  margin: 2em 0;
  padding: 1.5em;
  background: transparent;
  border: 1px solid #ad9160;
  border-radius: 9px;
  width: 100%;
  margin: 2em auto;
}

.toc-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.toc-list .toc-list {
  padding-left: 1.5em;
  margin-top: 0.5em;
}

.toc-item {
  margin: 0.5em 0;
  line-height: 1.6;
}

.toc-link {
  color: #ad9160;
  text-decoration: none;
  transition: color 0.2s ease;
  display: inline-block;
}

.toc-link:hover {
  color: #8c754b;
  text-decoration: underline;
}

.toc-level-1 > .toc-link {
  font-weight: 600;
  font-size: 1.05em;
}

.toc-level-2 > .toc-link {
  font-weight: 500;
}

.toc-level-3 > .toc-link,
.toc-level-4 > .toc-link {
  font-weight: 400;
  font-size: 0.95em;
}

.toc-empty {
  color: #999;
  font-style: italic;
  text-align: center;
  margin: 0;
}

.toc-hidden {
  display: none !important;
}

/* Citations and references */
.cite, .ref {
  color: var(--latex-link);
}

` +
  // PREAMBLE LINK: \renewcommand{\citation}[1]{\begin{quote}#1\end{quote}} (preambuleV3.tex:293)
  // and \setlength{\leftmargini}{25pt} (:166). This is where a citation actually lands in
  // production: the macro expands to a quote environment, environments.ts wraps it in
  // <blockquote class="quote-env">, and the left/right margin below is the \leftmargini value
  // read at settings.ts R-038 — so that preamble line IS honoured, here.
  // /!\ text-align: justify !important is hard-coded and overrides the .extract chain, exactly as
  // it does for `p` (see the note on the `p` rule above).
  String.raw`/* Quotes - better styling */
blockquote, blockquote.quote-env, blockquote.quotation-env, blockquote.verse-env {
  margin: 1em var(--latex-quote-margin);
  padding: 0 1em;
  background: transparent;
  font-style: normal;
  text-align: justify !important;
}

blockquote p, blockquote.verse-env p {
  text-indent: 0;
  text-align: justify !important;
}

/* Successive quotes reduced spacing */
blockquote.quote-successive-start {
  margin-bottom: 0;
}
blockquote.quote-successive-middle {
  margin-top: 0;
  margin-bottom: 0;
}
blockquote.quote-successive-end {
  margin-top: 0;
}

/* Verse environment - preserve line breaks */
.verse-env {
  white-space: pre-line;
}

` +
  // PREAMBLE LINK: \usepackage{pdflscape} (preambuleV3.tex:75), needed for the annexes of the
  // Commission's reference document. HARD-CODED — that line is never parsed (R-143 deletes every
  // \usepackage), yet the landscape environment IS handled: environments.ts step 16 wraps it in
  // this class, and the on-screen rule below fakes the rotation with a transform. The output is
  // therefore roughly right while the causal link is absent; editing the preamble line does
  // nothing. /!\ The print half is NOT right — see the @media print block at the end of this file.
  String.raw`/* Landscape sections */
.landscape {
  transform: rotate(90deg);
  transform-origin: left top;
  width: 100vh;
  height: 100vw;
  overflow-x: hidden;
  position: absolute;
  top: 100%;
  left: 0;
}

` +
  // PREAMBLE LINK: \usepackage{longtable} (preambuleV3.tex:69). HARD-CODED — never parsed, yet the
  // environment is fully rendered by environments.ts (convertLongtableEnvironments, R-295) and
  // styled here. page-break-inside on the rows is the web equivalent of "a table that may span
  // several pages". Editing or deleting the preamble line changes nothing.
  String.raw`/* Longtable - multi-page tables */
.longtable {
  width: 100%;
  page-break-inside: auto;
}

.longtable tr {
  page-break-inside: avoid;
  page-break-after: auto;
}

/* Minipage for side-by-side content */
.minipage {
  display: inline-block;
  vertical-align: top;
}

` +
  // PREAMBLE LINK: \FBsetspaces{colon}/{thin} (preambuleV3.tex:142-143), the French space before
  // : ; ! ?. EMITTED BY NOTHING — no code writes class="french-spacing", so this rule never
  // matches, and it would not honour the preamble even if it did: the content below is a plain
  // U+00A0, not the --french-colon-space / --french-thin-space values parsed from those lines.
  // This is the intended home of a fix for RK-38 / DF-13; the missing half is a converter pass
  // that wraps the punctuation, not anything in the preamble.
  String.raw`/* French punctuation spacing */
.french-spacing::before {
  content: "\00a0";
}

` +
  // PREAMBLE LINK: the print block below is where the preamble's page setup would have to live,
  // and three separate requests fail here.
  //   • \usepackage[all]{nowidow} (preambuleV3.tex:29) — HARD-CODED again, on body and on p.
  //   • \geometry{…} (:107) — NOT honoured. `margin: 0; padding: 0` discards the requested page
  //     size and margins outright, and there is no @page rule anywhere in this stylesheet, so the
  //     browser's own paper settings decide everything (RK-34 / DF-11).
  //   • \usepackage{pdflscape} (:75) — NOT honoured. `page-orientation` is not a CSS property at
  //     all; the declaration below is inert and a landscape annexe prints portrait. The real
  //     equivalent would be an @page rule with `size: landscape`, which does not exist here.
  String.raw`/* Print styles */
@media print {
  body {
    max-width: none;
    margin: 0;
    padding: 0;
    widows: 3;
    orphans: 3;
  }
  
  .page-break {
    page-break-after: always;
    border: none;
    margin: 0;
  }
  
  .marginpar {
    position: absolute;
    left: 5mm;
  }
  
  .landscape {
    transform: none;
    width: auto;
    height: auto;
    position: relative;
    page-orientation: landscape;
  }
  
  p {
    widows: 3;
    orphans: 3;
  }
  
  .page-break {
    page-break-after: always;
    break-after: page;
    display: block;
    margin: 0;
  }

  .page-break img {
    display: none;
  }
}

@media screen {
  .page-break {
    display: block;
    text-align: center;
    margin: 4rem auto;
    border: none;
  }

  .page-break img {
    width: 15%;
    max-width: 100px;
    height: auto;
    opacity: 0.5;
  }
}
`
}
